import ReactGA from 'react-ga4';

const TRACKING_ID = 'G-X4DERZ8P39';

/** Analytics is opt-out via REACT_APP_GA_ENABLED=false (defaults to enabled). */
export function isAnalyticsEnabled() {
  const flag = String(process.env.REACT_APP_GA_ENABLED ?? 'true').trim().toLowerCase();
  return flag !== 'false' && flag !== '0' && flag !== 'no';
}

// Initialize Google Analytics
export const initGA = () => {
  if (!isAnalyticsEnabled()) return;
  if (TRACKING_ID && typeof window !== 'undefined') {
    ReactGA.initialize(TRACKING_ID);
  }
};

// Track page views
export const trackPageView = (path) => {
  if (!isAnalyticsEnabled()) return;
  if (typeof window !== 'undefined' && ReactGA.isInitialized) {
    ReactGA.set({ page: path });
    ReactGA.send('pageview', { page_path: path });
  }
};

// Track button clicks and other events
export const trackEvent = (eventName, eventParams = {}) => {
  if (!isAnalyticsEnabled()) return;
  if (typeof window !== 'undefined' && ReactGA.isInitialized) {
    ReactGA.event({
      action: eventName,
      category: eventParams.category || 'User Interaction',
      label: eventParams.label || '',
      value: eventParams.value || undefined,
      ...eventParams
    });
  }
};

// Track button clicks specifically
export const trackButtonClick = (buttonName, additionalParams = {}) => {
  trackEvent('button_click', {
    category: 'Button Click',
    label: buttonName,
    ...additionalParams
  });
};

// Track navigation clicks
export const trackNavigation = (destination, linkText) => {
  trackEvent('navigation_click', {
    category: 'Navigation',
    label: linkText || destination,
    destination: destination
  });
};

// Track add to cart events
export const trackAddToCart = (item) => {
  trackEvent('add_to_cart', {
    category: 'Ecommerce',
    label: item.title || item.id,
    item_id: item.id,
    item_name: item.title,
    item_category: item.service || item.category,
    value: parseFloat(item.price?.replace(/[^0-9.]/g, '')) || 0
  });
};

// Track checkout events
export const trackCheckout = (items, total) => {
  trackEvent('begin_checkout', {
    category: 'Ecommerce',
    value: total,
    items: items.map(item => ({
      item_id: item.id,
      item_name: item.title,
      item_category: item.service || item.category,
      price: parseFloat(item.price?.replace(/[^0-9.]/g, '')) || 0,
      quantity: item.quantity || 1
    }))
  });
};

// Track purchase events
export const trackPurchase = (transactionId, items, total) => {
  trackEvent('purchase', {
    category: 'Ecommerce',
    transaction_id: transactionId,
    value: total,
    items: items.map(item => ({
      item_id: item.id,
      item_name: item.title,
      item_category: item.service || item.category,
      price: parseFloat(item.price?.replace(/[^0-9.]/g, '')) || 0,
      quantity: item.quantity || 1
    }))
  });
};

// Track favorite actions
export const trackFavorite = (item, isAdding) => {
  trackEvent('favorite', {
    category: 'User Action',
    label: isAdding ? 'Add to Favorites' : 'Remove from Favorites',
    item_id: item.id,
    item_name: item.title,
    item_category: item.service || item.category
  });
};

// Track search events
export const trackSearch = (searchTerm) => {
  trackEvent('search', {
    category: 'User Action',
    label: searchTerm,
    search_term: searchTerm
  });
};

// Track form submissions
export const trackFormSubmit = (formName, success = true) => {
  trackEvent('form_submit', {
    category: 'Form',
    label: formName,
    success: success
  });
};

// Track login/logout events
export const trackAuth = (action) => {
  trackEvent('auth', {
    category: 'User Action',
    label: action // 'login', 'logout', 'register'
  });
};

// Higher-order function to wrap button onClick handlers with tracking
export const withTracking = (onClick, buttonName, additionalParams = {}) => {
  return (event) => {
    trackButtonClick(buttonName, additionalParams);
    if (onClick) {
      onClick(event);
    }
  };
};

